import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";

export const HeroVideo = () => {
  const videoRef = useRef(null);
  const userPaused = useRef(false);
  const reduceMotion = useReducedMotion();
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    video.muted = true;
    userPaused.current = Boolean(reduceMotion);
    let visible = true;
    const syncPlayback = () => {
      if (visible && !document.hidden && !userPaused.current) {
        video.play().catch(() => setPlaying(false));
      } else {
        video.pause();
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      syncPlayback();
    }, { threshold: 0.05 });
    observer.observe(video);
    document.addEventListener("visibilitychange", syncPlayback);
    syncPlayback();
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", syncPlayback);
      video.pause();
    };
  }, [reduceMotion]);

  const toggle = () => {
    const video = videoRef.current;
    userPaused.current = !video.paused;
    if (video.paused) video.play().catch(() => setPlaying(false));
    else video.pause();
  };

  return (
    <>
      <video ref={videoRef} data-testid="hero-video" aria-label="A car detailer polishing a glossy black car"
        className="h-full w-full scale-105 object-cover" autoPlay={!reduceMotion} loop muted playsInline
        preload="metadata" poster="/hero-car-detailing-poster.jpg"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}>
        <source media="(max-width: 767px)" src="/hero-car-detailing-mobile.webm" type="video/webm" />
        <source media="(max-width: 767px)" src="/hero-car-detailing-mobile.mp4" type="video/mp4" />
        <source src="/hero-car-detailing.webm" type="video/webm" />
        <source src="/hero-car-detailing.mp4" type="video/mp4" />
      </video>
      <button type="button" data-testid="hero-video-toggle" onClick={toggle}
        aria-label={playing ? "Pause background video" : "Play background video"}
        title={playing ? "Pause video" : "Play video"}
        className="absolute bottom-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-black/60 text-white transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white md:bottom-8 md:right-8">
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </button>
    </>
  );
};